import {useCallback, useEffect, useState, type ReactNode} from 'react';
import {cn} from '@/matic/lib/utils/cn';

/**
 * Transition applied to every consumer-rendered push-buffer `div` (the elements
 * that set `paddingRight` from the render-prop width) so the centered canvas
 * eases in step with the drawer slide instead of snapping.
 *
 * Compose it onto the buffer `div`'s className via `cn`. It works through the
 * named `group/inspector` on this layout's root element:
 * - `transition-[padding]` — only the buffer eases, nothing else.
 * - `group-data-[resizing]/inspector:transition-none` — the layout sets
 *   `data-resizing` during an interactive drag, killing the ease so the buffer
 *   tracks the cursor with zero lag.
 * - `motion-reduce:transition-none` — respects reduced-motion, matching the
 *   panel slide and the LHS rail.
 *
 * Duration/easing (`duration-300 ease-out`) deliberately mirror the panel slide
 * so open/close feel like one coordinated motion.
 */
export const INSPECTOR_BUFFER_TRANSITION =
  'transition-[padding] duration-300 ease-out ' +
  'group-data-[resizing]/inspector:transition-none motion-reduce:transition-none';

type Props = {
  /**
   * Render prop — receives the effective panel width so the consumer can apply
   * the right-side buffer exactly where needed (e.g. only on the scrollable
   * area, not on a header that should span full width).
   *
   * The width passed is always `defaultWidth` when the panel is closed, so the
   * reserved buffer — and therefore the content column position — never changes
   * on panel open/close.
   *
   * IMPORTANT — the meaning of this width is mode-dependent:
   * - **Overlay mode** (the default; `reserveBreakpoint` not met): always `0`.
   *   The panel floats over the content, so there is no buffer to reserve and
   *   the consumer's `paddingRight` is a no-op.
   * - **Push mode** (`reserveBreakpoint` met): the live `panelWidth`. The
   *   consumer's `paddingRight` reserves a gutter and compresses the canvas.
   *
   * The second argument exposes metadata for cases that intentionally need the
   * physical overlay width while the first argument remains `0` (for example,
   * overlay-aware centering in a narrow workspace).
   */
  children: (
    panelWidth: number,
    state: {
      isOpen: boolean;
      isResizing: boolean;
      panelWidth: number;
      shouldReserveGutter: boolean;
    }
  ) => ReactNode;
  /**
   * Panel content. When provided the panel is visible. When absent the buffer
   * stays at `defaultWidth` so content never shifts on open/close.
   */
  panel?: ReactNode;
  defaultWidth?: number;
  minWidth?: number;
  maxWidth?: number;
  /**
   * When provided and the panel is open, the layout auto-expands to at least
   * this width (clamped to `maxWidth`). Only expands — never shrinks the panel
   * below the user's current drag position. Used by the Library for file vs.
   * thread previews.
   */
  targetWidth?: number;
  /**
   * Pin the panel to an exact width and suppress its drag handle. Used when the
   * panel is not a free-floating drawer but the right half of a split whose seam
   * is owned by something else — the Library's Otto dock, whose right edge the
   * panel abuts. The width is then *derived* (viewport − chrome − dock), so
   * there is one drag handle in the split, not two.
   *
   * `targetWidth` cannot do this job: it only ever expands (see above), so a
   * user who once dragged the panel wide would permanently break the abutment.
   * When set, this overrides the internal width entirely; unset restores the
   * ordinary `defaultWidth` + `targetWidth` + drag behavior.
   */
  pinnedWidth?: number;
  /**
   * When the width is pinned, this layout's own drag handle is unmounted, so
   * its internal `isResizing` (only ever set by that handle's mousedown) can
   * never fire — which silently defeats `INSPECTOR_BUFFER_TRANSITION` for the
   * whole time the external driver (the Otto dock) is being dragged: the
   * panel and dock track the cursor with zero lag, but the padding buffer
   * that reserves room for them keeps easing over 300ms behind a target that
   * moves every frame, reading as stutter/rubber-banding. Pass the external
   * driver's own drag state here so it can be OR'd into `isResizing`. No-op
   * when `pinnedWidth` is unset.
   */
  externalIsResizing?: boolean;
  /**
   * Override the panel's left edge (border + shadow). Defaults to a neutral
   * hairline + `shadow-lg`. The Library passes a soft navy-tinted shadow so the
   * inspector reads as a lifted, lightly-branded plane over its neutral-50
   * working surface.
   */
  edgeClassName?: string;
  /**
   * Minimum viewport width required to reserve the closed panel's gutter (push
   * mode). Below this width the panel overlays content instead of compressing it.
   * Defaults to never-reserve (overlay) — the safe mode where the panel floats,
   * has no layout impact, and `targetWidth` auto-expand always works. Pass a
   * pixel breakpoint (e.g. `1536`) to opt into push mode, or `0` to push at all
   * widths.
   */
  reserveBreakpoint?: number;
  /**
   * Keep the panel inside this layout's own box instead of the viewport's right
   * edge. Use where the layout sits below page chrome (a header, a step bar) that
   * the panel must not cover. Defaults to the viewport, as the Library uses it.
   */
  contained?: boolean;
};

/**
 * Overlay panel layout: the panel is fixed to the right edge of the viewport
 * and overlays into a permanently-reserved right buffer. Content never shifts
 * when the panel opens or closes at its default width — the buffer is always
 * there. The panel is drag-resizable; widening beyond the default buffer
 * increases the buffer proportionally, compressing the scroll area.
 * Closing the panel resets the panel width to defaultWidth so the left side
 * returns to its original layout.
 *
 * Use instead of ResizablePanelLayout when the panel should feel like an
 * inspector sliding into existing whitespace, not a peer in a flex split.
 */
export function OverlayPanelLayout({
  children,
  panel,
  defaultWidth = 420,
  minWidth = 320,
  maxWidth = 700,
  targetWidth,
  pinnedWidth,
  externalIsResizing = false,
  edgeClassName = 'border-l border-neutral-200 shadow-lg',
  reserveBreakpoint = Number.POSITIVE_INFINITY,
  contained = false,
}: Props) {
  const [panelWidth, setPanelWidth] = useState(defaultWidth);
  const [internalIsResizing, setIsResizing] = useState(false);
  const isResizing = internalIsResizing || externalIsResizing;
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof globalThis.innerWidth === 'number' ? globalThis.innerWidth : 0
  );
  const isOpen = panel !== undefined && panel !== null;
  const shouldReserveGutter = viewportWidth >= reserveBreakpoint;
  // A pinned panel is a half of someone else's split: its width is derived, not
  // dragged, so the internal width state (and the grip that edits it) stand
  // down. Everything else — slide, latch, reserve — is unchanged.
  const isPinned = pinnedWidth !== undefined;
  const livePanelWidth = isPinned
    ? Math.max(pinnedWidth, minWidth)
    : panelWidth;

  // Slide animation state. `mounted` keeps the panel in the tree through its
  // exit transition; `entered` drives the open/closed transform; `displayPanel`
  // latches the last content so it stays visible while sliding out (the `panel`
  // prop goes null the instant the consumer closes). Without the latch the
  // content would blank out before the slide finishes.
  const [mounted, setMounted] = useState(isOpen);
  const [entered, setEntered] = useState(isOpen);
  const [displayPanel, setDisplayPanel] = useState<ReactNode>(panel ?? null);

  useEffect(() => {
    if (isOpen) {
      setDisplayPanel(panel);
      setMounted(true);
      // Flip to the open transform on the next frame so the panel transitions
      // in from off-screen rather than appearing already-open.
      const id = requestAnimationFrame(() => {
        setEntered(true);
      });
      return () => {
        cancelAnimationFrame(id);
      };
    }

    // Closing: slide out now; unmount + reset happen on transitionEnd.
    setEntered(false);
  }, [isOpen, panel]);

  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (e.propertyName !== 'transform' || isOpen) return;
      setMounted(false);
      setDisplayPanel(null);
      // Reset width once fully closed so the next open starts from default and
      // the reserved gutter returns to its resting size.
      setPanelWidth(defaultWidth);
    },
    [isOpen, defaultWidth]
  );

  // The effective cap is the smaller of the caller's `maxWidth` and a fraction
  // of the viewport, so the panel can extend well past the old fixed cap on
  // wide screens while still leaving a sliver of the left surface visible.
  const resolveMaxWidth = useCallback(
    () => Math.min(maxWidth, Math.round(globalThis.innerWidth * 0.9)),
    [maxWidth]
  );

  // Auto-expand when targetWidth changes and the panel is open.
  // Only expands — Math.max preserves any wider user drag position.
  // Runs in both modes: in overlay mode (effectiveWidth 0) expanding has no
  // layout impact; in push mode (shouldReserveGutter) the gutter grows so the
  // content is compressed to make room — desired on wide screens where there's
  // ample width to give the preview its intended size. resolveMaxWidth still
  // caps the panel at 90% of the viewport so the left surface stays visible.
  // Skipped while pinned: `pinnedWidth` already governs the visible width (see
  // `livePanelWidth`), so letting this keep bumping the internal `panelWidth`
  // would silently inflate hidden state that resurfaces as a stale jump if the
  // panel is later unpinned without closing.
  useEffect(() => {
    if (!isOpen || targetWidth === undefined || isPinned) return;
    setPanelWidth((current) =>
      Math.max(current, Math.min(targetWidth, resolveMaxWidth()))
    );
  }, [isOpen, targetWidth, resolveMaxWidth, isPinned]);

  // Track viewport width for reserveBreakpoint and clamp panel width on resize.
  useEffect(() => {
    const onResize = () => {
      setViewportWidth(globalThis.innerWidth);
      if (isOpen)
        setPanelWidth((current) => Math.min(current, resolveMaxWidth()));
    };

    globalThis.addEventListener('resize', onResize);
    return () => {
      globalThis.removeEventListener('resize', onResize);
    };
  }, [isOpen, resolveMaxWidth]);

  // Reserve the gutter while the panel is mounted (including its slide-out) so
  // content doesn't reflow until the panel has fully left the screen.
  const effectiveWidth = shouldReserveGutter
    ? mounted
      ? livePanelWidth
      : defaultWidth
    : 0;

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = panelWidth;
      // Suppress the buffer ease while dragging so the canvas tracks the cursor
      // with zero lag (see INSPECTOR_BUFFER_TRANSITION).
      setIsResizing(true);

      const onMouseMove = (ev: MouseEvent) => {
        const delta = startX - ev.clientX;
        setPanelWidth(
          Math.max(minWidth, Math.min(resolveMaxWidth(), startWidth + delta))
        );
      };

      const onMouseUp = () => {
        setIsResizing(false);
        globalThis.removeEventListener('mousemove', onMouseMove);
        globalThis.removeEventListener('mouseup', onMouseUp);
      };

      globalThis.addEventListener('mousemove', onMouseMove);
      globalThis.addEventListener('mouseup', onMouseUp);
    },
    [panelWidth, minWidth, resolveMaxWidth]
  );

  return (
    <div
      className="group/inspector relative h-full"
      data-resizing={isResizing ? '' : undefined}
    >
      {children(effectiveWidth, {
        isOpen,
        isResizing,
        panelWidth: livePanelWidth,
        shouldReserveGutter,
      })}

      {mounted && (
        <div
          className={cn(
            contained ? 'absolute' : 'fixed',
            'inset-y-0 right-0 z-20 flex bg-white',
            'transition-transform duration-300 ease-out motion-reduce:transition-none',
            entered ? 'translate-x-0' : 'translate-x-full',
            edgeClassName
          )}
          style={{width: livePanelWidth}}
          onTransitionEnd={handleTransitionEnd}
        >
          {/* Drag handle on left edge. Suppressed when the width is pinned —
              the seam is then owned by the pane on the left (the Otto dock),
              and two grips on one seam would fight each other. */}
          {isPinned ? null : (
            <div
              className="group relative z-10 flex w-4 shrink-0 cursor-col-resize items-center justify-center bg-transparent"
              onMouseDown={handleResizeMouseDown}
            >
              {/* Divider line */}
              <div className="pointer-events-none absolute inset-y-0 left-0 w-px bg-neutral-200 transition-colors group-hover:bg-primary-400" />
              {/* Grip widget — straddles the left edge of the panel */}
              <div className="flex translate-x-[-6px] flex-col items-center">
                <div className="flex h-8 w-4 items-center justify-center rounded border border-neutral-200 bg-white shadow-sm group-hover:border-primary-300">
                  <div className="flex flex-col gap-0.5">
                    <div className="h-0.5 w-0.5 rounded-full bg-neutral-400 group-hover:bg-primary-500" />
                    <div className="h-0.5 w-0.5 rounded-full bg-neutral-400 group-hover:bg-primary-500" />
                    <div className="h-0.5 w-0.5 rounded-full bg-neutral-400 group-hover:bg-primary-500" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Panel content */}
          <div className="min-w-0 flex-1 overflow-hidden">{displayPanel}</div>
        </div>
      )}
    </div>
  );
}
